import React, { useEffect, useState, createElement } from 'react';
import { UserProvider, useUser } from './contexts/UserContext';
import { LoginPage } from './pages/LoginPage';
import { Header } from './components/Header';
import { MainTrainingCard } from './components/MainTrainingCard';
import { CallHistoryList } from './components/CallHistoryList';
import { SettingsPage } from './components/SettingsPage';
import { RepsScorePage } from './components/RepsScorePage';
import { TopCzarPage } from './components/TopCzarPage';
import { AdminLoginModal } from './components/AdminLoginModal';
import { AdminFilterPanel } from './components/AdminFilterPanel';
import { Toast } from './components/Toast';
import { BottomNav } from './components/BottomNav';
import { X } from 'lucide-react';
import { AgentFilters, selectAgentUrl, getAgentLabel } from './utils/agentMatrix';
// Training Lead type
type TrainingLead = {
  agentId: string;
  ownerName: string;
  businessName: string;
  industry: string;
};
// ElevenLabs Sales Training Leads
const OUTBOUND_LEADS: TrainingLead[] = [{
  agentId: 'agent_1201kbs9egktfy0972qmkekdzzxb',
  ownerName: 'Aerisita Carter',
  businessName: 'Polished & Proud Nail Studio',
  industry: 'Nail Salon'
}, {
  agentId: 'agent_0501kbs60tqhfmvvjgpsqe8fyjec',
  ownerName: 'Dr. Thomas Young',
  businessName: 'EQ Dental',
  industry: 'Dental Group'
}, {
  agentId: 'agent_2301kbs5rkmtf4waxdwafv4767na',
  ownerName: 'Walter Greene',
  businessName: 'Old Mill Antiques',
  industry: 'Antiques Shop'
}, {
  agentId: 'agent_6401kbs5d9bae38an6sry16a0ymy',
  ownerName: 'Jake Henderson',
  businessName: 'Ironclad General Contracting',
  industry: 'General Contractor'
}, {
  agentId: 'agent_5201kbpdsarmejr8nvt2atztacpy',
  ownerName: 'Sam & Mariska',
  businessName: 'Batika India Bistro',
  industry: 'Restaurant – Indian'
}];
// Helper to choose a non-repeating random lead
const LAST_AGENT_KEY = 'cczar:lastTrainingAgentId';
function getRandomLeadAvoidingLast(): TrainingLead {
  const lastAgentId = typeof window !== 'undefined' ? window.localStorage.getItem(LAST_AGENT_KEY) : null;
  if (OUTBOUND_LEADS.length === 1) {
    const onlyLead = OUTBOUND_LEADS[0];
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LAST_AGENT_KEY, onlyLead.agentId);
    }
    return onlyLead;
  }
  let candidate: TrainingLead;
  do {
    candidate = OUTBOUND_LEADS[Math.floor(Math.random() * OUTBOUND_LEADS.length)];
  } while (candidate.agentId === lastAgentId);
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(LAST_AGENT_KEY, candidate.agentId);
  }
  return candidate;
}
// Declare custom element for TypeScript
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'elevenlabs-convai': {
        'agent-id': string;
        'dynamic-variables'?: string;
        key?: string;
      };
    }
  }
}
// Mock call history data
const mockCallHistory = [{
  id: '1',
  talkTime: '07:23',
  callScore: 9.4,
  timestamp: new Date('2025-12-06T10:30:00'),
  leadType: 'Outbound',
  leadSource: 'Facebook',
  industry: 'Salon - Nail'
}, {
  id: '2',
  talkTime: '05:45',
  callScore: 5.6,
  timestamp: new Date('2025-12-06T09:15:00'),
  leadType: 'Inbound',
  leadSource: 'Google',
  industry: 'Dentist - General'
}, {
  id: '3',
  talkTime: '12:18',
  callScore: 8.2,
  timestamp: new Date('2025-12-05T16:45:00'),
  leadType: 'Outbound',
  leadSource: 'Instagram',
  industry: 'Antiques - Storefront'
}, {
  id: '4',
  talkTime: '08:52',
  callScore: 6.7,
  timestamp: new Date('2025-12-05T14:20:00'),
  leadType: 'Inbound',
  leadSource: 'Phone',
  industry: 'Restaurant - Indian'
}];
function AppContent() {
  const {
    currentUser,
    logout
  } = useUser();
  // State
  const [isAdminUnlocked, setIsAdminUnlocked] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showFiltersSummary, setShowFiltersSummary] = useState(false);
  const [currentTab, setCurrentTab] = useState('call');
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [activeLead, setActiveLead] = useState<TrainingLead | null>(null);
  const [dynamicVars, setDynamicVars] = useState<Record<string, unknown> | null>(null);
  // Toast State
  const [toast, setToast] = useState({
    show: false,
    message: ''
  });
  // Filter State
  const [filters, setFilters] = useState<AgentFilters>({
    leadType: 'Outbound',
    leadSource: 'Facebook',
    difficulty: 7,
    productInterest: 'CRM & Lead Management'
  });
  // Load ElevenLabs widget script on mount
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://elevenlabs.io/convai-widget/index.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      // Cleanup: remove script on unmount
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);
  // Clear toast when user changes (prevents logout message on login)
  useEffect(() => {
    setToast({
      show: false,
      message: ''
    });
  }, [currentUser]);
  // If not logged in, show login page
  if (!currentUser) {
    return <LoginPage data-id="element-0" />;
  }
  // Handlers
  const handleAdminClick = () => {
    if (isAdminUnlocked) {
      const filterPanel = document.getElementById('admin-panel');
      filterPanel?.scrollIntoView({
        behavior: 'smooth'
      });
    } else {
      setShowAdminModal(true);
    }
  };
  const handleUnlock = () => {
    setIsAdminUnlocked(true);
    setShowAdminModal(false);
    setToast({
      show: true,
      message: 'Admin mode unlocked'
    });
  };
  const handleStartCall = () => {
    // Check if outbound is selected
    if (filters.leadType.toLowerCase() !== 'outbound') {
      setToast({
        show: true,
        message: 'Inbound training coming soon'
      });
      return;
    }
    // Pick random training lead (avoiding last one used)
    const randomLead = getRandomLeadAvoidingLast();
    // Set agent ID, lead info, and dynamic variables
    setSelectedAgentId(randomLead.agentId);
    setActiveLead(randomLead);
    setDynamicVars({
      training__lead_type: filters.leadType.toLowerCase(),
      training__lead_source: filters.leadSource.toLowerCase(),
      training__difficulty: filters.difficulty,
      training__product_interest: filters.productInterest || ''
    });
  };
  const handleEditFilters = () => {
    if (!isAdminUnlocked) {
      setShowAdminModal(true);
    } else {
      const filterPanel = document.getElementById('admin-panel');
      filterPanel?.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleLogout = () => {
    logout();
    setToast({
      show: true,
      message: 'Logged out successfully'
    });
  };
  const handleViewCallDetails = (callId: string) => {
    setToast({
      show: true,
      message: `Viewing details for call ${callId}`
    });
  };
  return <div className="min-h-screen bg-gray-200 flex items-center justify-center p-0 sm:p-8 font-sans" data-id="element-1">
      {/* Phone Frame Container */}
      <div className="w-full max-w-[400px] bg-[#eef0f5] sm:rounded-[40px] shadow-2xl overflow-hidden min-h-[100vh] sm:min-h-[850px] relative flex flex-col" data-id="element-2">
        {/* Background Gradient Spot (Subtle) */}
        <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-white/40 to-transparent pointer-events-none" data-id="element-3" />

        <Header onAdminClick={handleAdminClick} isAdminUnlocked={isAdminUnlocked} data-id="element-4" />

        <main className="flex-grow flex flex-col relative z-10 overflow-y-auto scrollbar-hide pb-24" data-id="element-5">
          {/* Conditional Content Based on Current Tab */}
          {currentTab === 'call' && <>
              <MainTrainingCard onStartCall={handleStartCall} filters={filters} onToggleFilters={() => setShowFiltersSummary(!showFiltersSummary)} showFilters={showFiltersSummary} isAdminUnlocked={isAdminUnlocked} data-id="element-6" />

              {/* Call History List */}
              <CallHistoryList calls={mockCallHistory} onViewDetails={handleViewCallDetails} data-id="element-7" />

              {/* Admin Panel - Slides up or appears when unlocked */}
              {isAdminUnlocked && <div id="admin-panel" className="px-6 pb-24 animate-in slide-in-from-bottom-10 fade-in duration-500" data-id="element-8">
                  <AdminFilterPanel filters={filters} onChange={setFilters} data-id="element-9" />
                </div>}
            </>}

          {currentTab === 'reps-score' && <RepsScorePage data-id="element-10" />}

          {currentTab === 'settings' && <>
              <SettingsPage filters={filters} onEditFilters={handleEditFilters} onLogout={handleLogout} isAdminUnlocked={isAdminUnlocked} data-id="element-11" />

              {/* Admin Panel also available in Settings */}
              {isAdminUnlocked && <div id="admin-panel" className="px-6 pb-24 animate-in slide-in-from-bottom-10 fade-in duration-500" data-id="element-12">
                  <AdminFilterPanel filters={filters} onChange={setFilters} data-id="element-13" />
                </div>}
            </>}

          {currentTab === 'leader-board' && <TopCzarPage data-id="element-14" />}
        </main>

        <BottomNav currentTab={currentTab} onTabChange={setCurrentTab} data-id="element-15" />

        {/* Modals & Overlays */}
        <AdminLoginModal isOpen={showAdminModal} onClose={() => setShowAdminModal(false)} onUnlock={handleUnlock} data-id="element-16" />

        <Toast message={toast.message} isVisible={toast.show} onClose={() => setToast({
        ...toast,
        show: false
      })} data-id="element-17" />

        {/* ElevenLabs Call Widget Overlay with Lead Info */}
        {selectedAgentId && dynamicVars && activeLead && <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 sm:p-4 py-8" data-id="element-18">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[85vh] sm:h-[80vh] flex flex-col overflow-hidden" data-id="element-19">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50" data-id="element-20">
                <span className="font-semibold text-sm text-gray-900" data-id="element-21">
                  Live Call – Sales Training Agent
                </span>
                <button className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-gray-300 hover:bg-gray-100 transition-colors font-medium text-gray-700" onClick={() => {
              setSelectedAgentId(null);
              setActiveLead(null);
              setDynamicVars(null);
            }} data-id="element-22">
                  <X className="w-4 h-4" data-id="element-23" />
                  End Call
                </button>
              </div>

              {/* Training Lead Info Card */}
              <div className="px-4 py-3 border-b bg-blue-50" data-id="element-24">
                <p className="text-xs font-semibold uppercase text-blue-600 tracking-wide mb-1" data-id="element-25">
                  New Lead for {currentUser?.name}
                </p>
                <div className="flex flex-col gap-0.5" data-id="element-26">
                  <span className="text-sm font-semibold text-gray-900" data-id="element-27">
                    {activeLead.ownerName}
                  </span>
                  <span className="text-sm text-gray-700" data-id="element-28">
                    {activeLead.businessName}
                  </span>
                  <span className="text-xs text-gray-500" data-id="element-29">
                    {activeLead.industry}
                  </span>
                </div>
              </div>

              {/* ElevenLabs Widget */}
              <div className="flex-1 overflow-hidden" data-id="element-30">
                <elevenlabs-convai key={selectedAgentId} agent-id={selectedAgentId} dynamic-variables={JSON.stringify(dynamicVars)} data-id="element-31" />
              </div>
            </div>
          </div>}
      </div>
    </div>;
}
export function App() {
  return <UserProvider data-id="element-32">
      <AppContent data-id="element-33" />
    </UserProvider>;
}